
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    MessageBar,
    MessageBarBody,
    Spinner,
    Text,
    Textarea,
    makeStyles,
    tokens,
} from '@fluentui/react-components';

// component functions
import * as ComponentFunctions from './SettlementChangeStatusConfirmation.Form.Functions';

// styles
import './SettlementChangeStatusConfirmation.Form.Styles.css';

// Components
import { DateTimeInput } from '../../../commons/component/Inputs/DateTimeInput/DateTimeInput';
import { SettlementChangeStatus } from '../../../models/Entities/Settlements/ChangeStatus/SettlementChangeStatus';
import { SettlementStatusEnum } from '../../../models/Enums/SettlementStatusEnum';
import { CurrencySelectorComponent } from '../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import { SettlementTotal } from '../../../models/Entities/SettlementTotals/SettlementTotals';
import CurrencyAmountComponent from '../../../commons/component/CurrencyAmount/CurrencyAmount.Component';

const useStyles = makeStyles({
    surface: {
        minWidth: '700px',
        maxWidth: '820px',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        paddingTop: tokens.spacingVerticalS,
    },
    twoColRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalL,
        alignItems: 'start',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    sectionLabel: {
        display: 'block',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase100,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        paddingBottom: tokens.spacingVerticalXXS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        marginBottom: tokens.spacingVerticalXXS,
    },
    totalsBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: tokens.borderRadiusSmall,
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    totalsItem: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    totalResultBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: tokens.colorBrandBackground2,
        borderRadius: tokens.borderRadiusMedium,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        border: `1px solid ${tokens.colorBrandStroke2}`,
        marginTop: tokens.spacingVerticalXS,
    },
    totalResultLabel: {
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
    },
    totalResultAmount: {
        fontWeight: tokens.fontWeightBold,
        fontSize: tokens.fontSizeBase400,
        color: tokens.colorBrandForeground1,
    },
    inlineFields: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'flex-end',
    },
    flexGrow: {
        flexGrow: 1,
    },
    buttonContent: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
    },
    descriptionBar: {
        marginBottom: tokens.spacingVerticalS,
    },
});


const SettlementChangeStatusConfirmationFormComponent = (props: ComponentFunctions.Props) => {

    const styles = useStyles();

    // Confirmation Form properties
    const [vaucherNumber, setVaucherNumber] = useState<string>();
    const [businessName, setBusinessName] = useState<string>();
    const [vaucherDate, setVaucherDate] = useState<Date>();
    const [markDebtAsCollected, setMarkDebtAsCollected] = useState<boolean>(false);
    const [dateCollected, setDateCollected] = useState<Date>();
    const [convertToSpecifiedCurrency, setConvertToSpecifiedCurrency] = useState<boolean>(false);
    const [specifiedCurrencyCode, setSpecifiedCurrencyCode] = useState<string>();
    const [specifiedExchangeRate, setSpecifiedExchangeRate] = useState<number>(1);
    const [comments, setComments] = useState<string>();
    // Totales del comprobante: opcionales, el servidor los guarda junto a lo calculado
    const [reportInvoiceTotals, setReportInvoiceTotals] = useState<boolean>(false);
    const [invoiceTotal, setInvoiceTotal] = useState<string>('');
    const [invoiceTax, setInvoiceTax] = useState<string>('');
    const [invoiceRetention, setInvoiceRetention] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const savingRef = useRef<boolean>(false);
    const prevIsChangingRef = useRef<boolean>(false);

    useEffect(() => {
        const wasChanging = prevIsChangingRef.current;
        prevIsChangingRef.current = props.isChangingStatus;

        if (savingRef.current && wasChanging && !props.isChangingStatus) {
            savingRef.current = false;
            setIsOpen(false);
        }
    }, [props.isChangingStatus]);

    const handleConfirmStatusChange = (event: MouseEvent<HTMLButtonElement>) => {
        if (props.Settlement === undefined) return;

        savingRef.current = true;

        const newStatus: SettlementChangeStatus = {
            settlementId: props.Settlement.id,
            voucheredNumber: vaucherNumber,
            bussinessname: businessName ?? "",
            status: props.NextStatusIsActionConfirmed === SettlementStatusEnum.PAID
                ? SettlementStatusEnum.CONFIRMED
                : props.NextStatusIsActionConfirmed,
            markAsCollected: markDebtAsCollected,
            collectionDate: dateCollected,
            convertToSpecifiedCurrency: convertToSpecifiedCurrency,
            specifiedCurrencyCode: specifiedCurrencyCode,
            specifiedExchangeRate: specifiedExchangeRate,
            comments: comments,
            ...(reportInvoiceTotals ? ComponentFunctions.invoiceTotalsCommand(invoiceTotal, invoiceTax, invoiceRetention) : {}),
        };

        props.ChangeSettlementStatus(newStatus);
    };

    useEffect(() => {
        setVaucherNumber(props.Settlement?.voucheredNumber || "");
        setBusinessName(props.Settlement?.bussinessname || "");
    }, [props.Settlement?.voucheredNumber, props.Settlement?.bussinessname]);

    const isDestructive = ComponentFunctions.isDestructiveAction(props.NextStatusIsActionConfirmed);
    const descriptionIntent = ComponentFunctions.getDescriptionIntent(props.NextStatusIsActionConfirmed);
    const triggerAppearance = ComponentFunctions.getTriggerButtonAppearance(props.NextStatusIsActionConfirmed);
    const currencyDisplay = ((specifiedCurrencyCode || props.Settlement?.currencyCode) || 'ARS').toUpperCase();

    const totalDeuda = ((props.Settlement?.totals || []).reduce((sum: number, t: SettlementTotal) => {
        const rounded = Number(t.ownerAmountWithholdingTotal.toFixed(2));
        const amount = (t.currencyCode?.toUpperCase() === (specifiedCurrencyCode || '').toUpperCase())
            ? rounded
            : rounded * (specifiedExchangeRate || 1);
        return sum + amount;
    }, 0)).toFixed(2);

    const ConfirmationForm = (
        <div className={styles.form}>

            {/* ── Fila 1: Comprobante (ancho completo) ──────────────────── */}
            <div className={styles.section}>
                <Text className={styles.sectionLabel}>Comprobante</Text>
                <Field label="Número de factura / comprobante">
                    <Input
                        size="medium"
                        type="text"
                        value={vaucherNumber}
                        placeholder="Ej: 0001-00012345"
                        onChange={(e) => setVaucherNumber(e.target.value)}
                    />
                </Field>
                <Field label="Razón social">
                    <Input
                        type="text"
                        value={businessName}
                        onClick={(e) => e.currentTarget.select()}
                        onChange={(e) => setBusinessName(e.target.value)}
                    />
                </Field>
                <Field>
                    <Checkbox
                        checked={reportInvoiceTotals}
                        onChange={(_, data) => setReportInvoiceTotals(data.checked as boolean ?? false)}
                        label="Informar los totales del comprobante"
                    />
                </Field>
                {reportInvoiceTotals && (
                    <div className={styles.inlineFields}>
                        <Field label="Total del comprobante" className={styles.flexGrow} hint="Lo que dice la factura, aunque difiera de lo calculado">
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={invoiceTotal}
                                onClick={(e) => e.currentTarget.select()}
                                onChange={(e) => setInvoiceTotal(e.target.value)}
                            />
                        </Field>
                        <Field label="Impuestos" className={styles.flexGrow}>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={invoiceTax}
                                onClick={(e) => e.currentTarget.select()}
                                onChange={(e) => setInvoiceTax(e.target.value)}
                            />
                        </Field>
                        <Field label="Retenciones" className={styles.flexGrow}>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={invoiceRetention}
                                onClick={(e) => e.currentTarget.select()}
                                onChange={(e) => setInvoiceRetention(e.target.value)}
                            />
                        </Field>
                    </div>
                )}
            </div>

            {/* ── Fila 2: dos columnas ──────────────────────────────────── */}
            <div className={styles.twoColRow}>

                {/* Columna izquierda: Gestión de cobro */}
                <div className={styles.section}>
                    <Text className={styles.sectionLabel}>Gestión de cobro</Text>
                    <Field>
                        <Checkbox
                            checked={markDebtAsCollected}
                            onChange={(_, data) => setMarkDebtAsCollected(data.checked as boolean ?? false)}
                            label="Marcar liquidación como cobrada"
                        />
                    </Field>
                    {markDebtAsCollected && (
                        <Field label="Fecha de cobro">
                            <DateTimeInput
                                label={undefined}
                                placeHolder={undefined}
                                selectedDate={dateCollected}
                                isSubmitted={false}
                                isRequired={false}
                                errorMessage={undefined}
                                onSelectDate={(date: Date | undefined) => setDateCollected(date)}
                            />
                        </Field>
                    )}
                </div>

                {/* Columna derecha: Conversión de moneda */}
                <div className={styles.section}>
                <Text className={styles.sectionLabel}>Conversión de moneda</Text>
                <Field>
                    <Checkbox
                        checked={convertToSpecifiedCurrency}
                        onChange={(_, data) => setConvertToSpecifiedCurrency(data.checked as boolean ?? false)}
                        label="Registrar deuda en una moneda diferente"
                    />
                </Field>
                {convertToSpecifiedCurrency && (
                    <>
                        <div className={styles.totalsBox}>
                            <Text weight="semibold" size={200} block>Montos originales de la liquidación</Text>
                            {props.Settlement?.totals.map((total: SettlementTotal) => (
                                <Text key={total.currencyCode} className={styles.totalsItem} block>
                                    <CurrencyAmountComponent amount={total.ownerAmountWithholdingTotal} currency={total.currencyCode} />
                                </Text>
                            ))}
                        </div>
                        <div className={styles.inlineFields}>
                            <div className={styles.flexGrow}>
                                <CurrencySelectorComponent
                                    errorMessage={undefined}
                                    defaultSelected={props.Settlement?.currencyCode}
                                    label="Moneda destino"
                                    onCurrencyChange={(code: string | undefined) => setSpecifiedCurrencyCode(code)}
                                />
                            </div>
                            <Field label="Tipo de cambio" className={styles.flexGrow}>
                                <Input
                                    type="number"
                                    step="0.0000001"
                                    min="0.000000000001"
                                    defaultValue="1"
                                    onClick={(e) => e.currentTarget.select()}
                                    onChange={(e) => setSpecifiedExchangeRate(Number(e.target.value))}
                                />
                            </Field>
                        </div>
                        <div className={styles.totalResultBox}>
                            <Text className={styles.totalResultLabel}>Total de deuda convertida</Text>
                            <Text className={styles.totalResultAmount}>
                                {totalDeuda} {currencyDisplay}
                            </Text>
                        </div>
                    </>
                )}
                </div>
            </div>

            {/* ── Fila 3: Observaciones (ancho completo) ────────────────── */}
            <div className={styles.section}>
                <Text className={styles.sectionLabel}>Observaciones</Text>
                <Field label="Comentarios adicionales">
                    <Textarea
                        placeholder="Ingrese comentarios adicionales..."
                        value={comments}
                        resize="vertical"
                        onChange={(e) => setComments(e.target.value)}
                    />
                </Field>
            </div>
        </div>
    );

    return (
        <Dialog modalType="modal" open={isOpen} onOpenChange={(_, data) => { if (!props.isChangingStatus) setIsOpen(data.open); }}>
            <DialogTrigger disableButtonEnhancement>
                {props.Settlement && (
                    <Button
                        appearance={triggerAppearance}
                        style={isDestructive ? { color: '#C50F1F', borderColor: '#C50F1F' } : undefined}
                        disabled={props.disabled}
                        onClick={() => setIsOpen(true)}
                    >
                        {ComponentFunctions.getTriggerTextButtonByStatus(props.NextStatusIsActionConfirmed)}
                    </Button>
                )}
            </DialogTrigger>
            <DialogSurface className={styles.surface} aria-describedby={undefined}>
                <DialogBody>
                    <DialogTitle>
                        {ComponentFunctions.GetActionTitleText(props.NextStatusIsActionConfirmed)}
                    </DialogTitle>
                    <DialogContent>
                        <MessageBar
                            intent={descriptionIntent}
                            className={styles.descriptionBar}
                        >
                            <MessageBarBody>
                                {ComponentFunctions.GetActionDescriptionText(props.NextStatusIsActionConfirmed)}
                            </MessageBarBody>
                        </MessageBar>
                        {(props.NextStatusIsActionConfirmed === SettlementStatusEnum.CONFIRMED ||
                            props.NextStatusIsActionConfirmed === SettlementStatusEnum.PAID) && ConfirmationForm}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            appearance="primary"
                            style={isDestructive ? { backgroundColor: '#C50F1F', borderColor: '#C50F1F' } : undefined}
                            onClick={handleConfirmStatusChange}
                            disabled={props.isChangingStatus}
                        >
                            <span className={styles.buttonContent}>
                                {props.isChangingStatus && <Spinner size="tiny" />}
                                {ComponentFunctions.GetConfirmedActionButtonText(props.NextStatusIsActionConfirmed)}
                            </span>
                        </Button>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary" disabled={props.isChangingStatus}>Cancelar</Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(SettlementChangeStatusConfirmationFormComponent as any);